import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Hexagon, KeyRound, Loader2, ArrowRight } from "lucide-react";
import { useCSuiteAuth } from "../state/CSuiteAuthContext";
import { toast } from "../lib/utils";

/**
 * Harmonizer login screen — single shared account, styled to match the cover
 * page. Uses the existing csuite auth backend under the hood.
 */
export default function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const { login } = useCSuiteAuth();
  const params = new URLSearchParams(loc.search);
  const nextUrl = params.get("next") || "/projects";
  const [email, setEmail] = useState("");
  const [password, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast("Enter email and password");
    setLoading(true);
    try {
      await login(email, password);
      nav(nextUrl, { replace: true });
    } catch (err) {
      toast(err?.response?.data?.detail || "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid-bg relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="absolute -top-32 -left-32 w-[640px] h-[640px] rounded-full blur-3xl opacity-60"
             style={{ background: "radial-gradient(closest-side, rgba(3,105,161,0.30), transparent)" }} />
        <div className="absolute -bottom-40 right-1/3 w-[600px] h-[600px] rounded-full blur-3xl opacity-50"
             style={{ background: "radial-gradient(closest-side, rgba(6,182,212,0.26), transparent)" }} />
      </div>

      <div className="relative z-10 min-h-screen grid place-items-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 brand-chip grid place-items-center shadow-sm"><Hexagon size={18} strokeWidth={2.2} /></div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">The SAP delivery operating system</div>
              <div className="font-display font-extrabold text-2xl tracking-tight text-zinc-900 leading-none">Harmonizer</div>
            </div>
          </div>

          <div className="card p-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Sign in</div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight text-zinc-900 mb-6">Enter Harmonizer</h1>

            <form onSubmit={submit} className="space-y-4">
              <label className="block">
                <span className="text-xs text-zinc-500">Email</span>
                <input
                  type="email" autoComplete="email"
                  className="fld mt-1" value={email} onChange={e => setEmail(e.target.value)}
                  data-testid="login-email"
                  placeholder="you@example.com"
                />
              </label>
              <label className="block">
                <span className="text-xs text-zinc-500">Password</span>
                <input
                  type="password" autoComplete="current-password"
                  className="fld mt-1" value={password} onChange={e => setPw(e.target.value)}
                  data-testid="login-password"
                  placeholder="••••••••"
                />
              </label>
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 font-medium px-5 py-3 rounded-sm transition-all shadow-lg disabled:opacity-60"
                style={{ background: "var(--navy)", color: "#FAFAFA" }}
                data-testid="login-submit"
              >
                {loading ? <><Loader2 size={14} className="animate-spin"/> Signing in…</> :
                  <><KeyRound size={14}/> Sign in <ArrowRight size={14}/></>}
              </button>
            </form>

            <div className="mt-6 font-mono text-[10px] text-zinc-400 leading-relaxed">
              Single-tenant shared login. For separate C-Suite executive access, use the C-Suite Portal from the cover page.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
